import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { useCart } from "@/hooks/useCart";
import { useThemeColor } from "@/hooks/useThemeColor";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import WhatsAppFloat from "@/pages/home/components/WhatsAppFloat";
import AnnouncementBar from "@/pages/home/components/AnnouncementBar";

const categoryLabels: Record<string, string> = {
  PS5: "PS5",
  PS4: "PS4",
  PC: "كمبيوتر ألعاب",
  Controllers: "أيادي التحكم",
  Headsets: "سماعات",
};

const FAVORITES_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "المفضلة — متجر الإبداع",
  "description": "قائمة المنتجات المفضلة لديك في متجر الإبداع AL Ebdaa — احفظ منتجاتك المفضلة وارجع إليها في أي وقت.",
  "url": `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/favorites`,
  "inLanguage": "ar-SA",
  "isPartOf": {
    "@type": "WebSite",
    "name": "متجر الإبداع AL Ebdaa",
    "url": import.meta.env.VITE_SITE_URL ?? "https://example.com"
  }
};

export default function FavoritesPage() {
  const [barVisible, setBarVisible] = useState(true);
  const handleBarVisibility = useCallback((v: boolean) => setBarVisible(v), []);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const { primary, hexAlpha } = useThemeColor();

  const { items, removeFavorite, clearFavorites, totalItems } = useFavorites();
  const { addItem, items: cartItems } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-favorites";
    script.textContent = JSON.stringify(FAVORITES_SCHEMA);
    document.head.appendChild(script);
    document.title = "المفضلة — متجر الإبداع AL Ebdaa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "قائمة المنتجات المفضلة لديك في متجر الإبداع AL Ebdaa — احفظ منتجاتك المفضلة وارجع إليها في أي وقت.");
    return () => {
      const el = document.getElementById("schema-favorites");
      if (el) el.remove();
    };
  }, []);

  const isInCart = (id: number) => cartItems.some((i) => i.id === id);

  const handleAddToCart = (item: typeof items[0]) => {
    addItem({
      id: item.id,
      nameAr: item.nameAr,
      price: item.price,
      image: item.image,
      category: item.category,
    });
  };

  const handleAddAllToCart = () => {
    items.forEach((item) => {
      if (!isInCart(item.id)) {
        addItem({
          id: item.id,
          nameAr: item.nameAr,
          price: item.price,
          image: item.image,
          category: item.category,
        });
      }
    });
  };

  const totalSavings = items.reduce((sum, item) => {
    return sum + Math.max(0, item.originalPrice - item.price);
  }, 0);

  const totalValue = items.reduce((sum, item) => sum + item.price, 0);

  return (
    <div dir="rtl" className="bg-[#0a0a0a] min-h-screen">
      <AnnouncementBar onVisibilityChange={handleBarVisibility} />
      <Navbar barVisible={barVisible} />

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-xs tracking-widest uppercase mb-1" style={{ fontFamily: "'Cairo', sans-serif", color: primary }}>
                قائمتي
              </p>
              <h1 className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: "'Cairo', sans-serif" }}>
                المفضلة
              </h1>
              {totalItems > 0 && (
                <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {totalItems} منتج محفوظ
                </p>
              )}
            </div>

            {totalItems > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleAddAllToCart}
                  className="flex items-center gap-2 px-5 py-2.5 text-black text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif", background: primary }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.85"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                >
                  <i className="ri-shopping-cart-line"></i>
                  أضف الكل للسلة
                </button>
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-white/10 hover:border-red-500/40 text-gray-500 hover:text-red-400 text-sm rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-delete-bin-line"></i>
                  مسح الكل
                </button>
              </div>
            )}
          </div>

          {/* Empty State */}
          {totalItems === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div className="w-24 h-24 flex items-center justify-center bg-[#141414] rounded-full mb-6 border border-white/5">
                <i className="ri-heart-line text-4xl text-gray-600"></i>
              </div>
              <h2 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                قائمة المفضلة فارغة
              </h2>
              <p className="text-gray-500 text-sm mb-8 max-w-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                احفظ المنتجات التي تعجبك بالضغط على أيقونة القلب وارجع إليها في أي وقت
              </p>
              <button
                onClick={() => navigate("/products")}
                className="px-6 py-3 text-black font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif", background: primary }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.85"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
              >
                تصفّح المنتجات
              </button>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-6">

              {/* Products List */}
              <div className="flex-1 space-y-4">
                {items.map((item) => {
                  const discount = item.originalPrice > item.price
                    ? Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)
                    : 0;
                  const inCart = isInCart(item.id);

                  return (
                    <div
                      key={item.id}
                      className="bg-[#141414] border border-white/5 rounded-xl overflow-hidden transition-all duration-300 flex"
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.2); }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)"; }}
                    >
                      {/* Image */}
                      <div className="relative w-36 sm:w-48 shrink-0 bg-[#1a1a1a]">
                        {item.image ? (
                          <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
                        ) : (
                          <div className="w-full h-full min-h-[140px] flex items-center justify-center">
                            <i className="ri-image-line text-gray-700 text-3xl"></i>
                          </div>
                        )}
                        {item.badge && (
                          <span className="absolute top-2 right-2 text-black text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif", background: primary }}>
                            {item.badge}
                          </span>
                        )}
                        {discount > 0 && (
                          <span className="absolute bottom-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            -{discount}%
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
                        <div>
                          <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            {categoryLabels[item.category] ?? item.category}
                          </p>
                          <Link
                            to={`/product/${item.id}`}
                            className="text-white font-bold text-base transition-colors"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = primary; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "#fff"; }}
                          >
                            {item.nameAr}
                          </Link>
                          <div className="flex items-center gap-1 mt-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-xs" style={{ color: i < item.rating ? primary : "#374151" }}></i>
                            ))}
                            <span className="text-gray-500 text-xs mr-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              ({item.reviews})
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
                          <div>
                            <span className="font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif", color: primary }}>
                              {item.price.toLocaleString()} ر.س
                            </span>
                            {item.originalPrice > item.price && (
                              <span className="text-gray-600 text-sm line-through mr-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                {item.originalPrice.toLocaleString()}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => removeFavorite(item.id)}
                              className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-red-500/40 text-gray-500 hover:text-red-400 rounded-lg transition-all cursor-pointer"
                              title="إزالة من المفضلة"
                            >
                              <i className="ri-heart-fill text-sm text-red-400"></i>
                            </button>
                            <button
                              onClick={() => handleAddToCart(item)}
                              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                                inCart ? "bg-green-500/20 text-green-400 border border-green-500/30" : "text-black"
                              }`}
                              style={!inCart ? { fontFamily: "'Cairo', sans-serif", background: primary } : { fontFamily: "'Cairo', sans-serif" }}
                            >
                              <i className={inCart ? "ri-check-line" : "ri-shopping-cart-line"}></i>
                              {inCart ? "في السلة" : "أضف للسلة"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary Sidebar */}
              <div className="w-full lg:w-72 shrink-0">
                <div className="bg-[#141414] border border-white/5 rounded-xl p-5 sticky top-24">
                  <h3 className="text-white font-bold text-base mb-5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    ملخص المفضلة
                  </h3>

                  <div className="space-y-3 mb-5">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>عدد المنتجات</span>
                      <span className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>{totalItems}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>إجمالي القيمة</span>
                      <span className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        {totalValue.toLocaleString()} ر.س
                      </span>
                    </div>
                    {totalSavings > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>توفيرك</span>
                        <span className="text-green-400 font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {totalSavings.toLocaleString()} ر.س
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-white/5 pt-4 mb-5">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>الإجمالي</span>
                      <span className="font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif", color: primary }}>
                        {totalValue.toLocaleString()} ر.س
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleAddAllToCart}
                    className="w-full py-3 text-black font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm"
                    style={{ fontFamily: "'Cairo', sans-serif", background: primary }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.85"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                  >
                    <i className="ri-shopping-cart-line ml-2"></i>
                    أضف الكل للسلة
                  </button>

                  <Link
                    to="/products"
                    className="flex items-center justify-center gap-2 w-full mt-3 py-2.5 border border-white/10 text-gray-400 text-sm rounded-xl transition-all cursor-pointer"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.3); (e.currentTarget as HTMLElement).style.color = primary; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; (e.currentTarget as HTMLElement).style.color = "#9ca3af"; }}
                  >
                    <i className="ri-arrow-left-line"></i>
                    متابعة التسوق
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Clear Confirm Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-red-500/10 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-400"></i>
            </div>
            <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
              مسح المفضلة؟
            </h3>
            <p className="text-gray-500 text-sm mb-6" style={{ fontFamily: "'Cairo', sans-serif" }}>
              سيتم حذف جميع المنتجات المحفوظة من قائمة المفضلة
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 border border-white/10 text-gray-400 hover:text-white rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                إلغاء
              </button>
              <button
                onClick={() => { clearFavorites(); setShowClearConfirm(false); }}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                مسح الكل
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
